import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../../services/api';
import HelpTooltip from '../ui/HelpTooltip';

// Etichete scurte per tip de instruire (aliniat cu MyCourses.js).
const TYPE_SHORT = {
  IIG_SSM: 'IIG SSM',
  ILM_SSM: 'ILM SSM',
  IIG_SU: 'IIG SU',
  ILM_SU: 'ILM SU',
  IP_SSM: 'IP SSM',
  IP_SU: 'IP SU',
  SUPLIMENTARE_SSM: 'Suplimentară SSM',
  SUPLIMENTARE_SU: 'Suplimentară SU',
};

// Bară de progres reutilizabilă (module completate / total). Refolosește datele din my-compliance.
export const ProgressBar = ({ completed, total, percent, className = '' }) => {
  const pct =
    typeof percent === 'number' ? percent : total > 0 ? Math.round((completed / total) * 100) : 0;
  const color =
    pct >= 100 ? 'bg-accent-500' : pct > 0 ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600';
  return (
    <div className={className}>
      <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
};

// Mini-semafor vertical (3 cercuri) — cel activ după severitate. Reutilizat din pattern-ul Dashboard.
const MiniTrafficLight = ({ status }) => (
  <span className="flex flex-col gap-0.5 flex-shrink-0" aria-hidden="true">
    <span
      className={`block w-2.5 h-2.5 rounded-full ${status === 'red' ? 'bg-danger-500' : 'bg-slate-300 dark:bg-slate-600'}`}
    ></span>
    <span
      className={`block w-2.5 h-2.5 rounded-full ${status === 'yellow' ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}
    ></span>
    <span
      className={`block w-2.5 h-2.5 rounded-full ${status === 'green' ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
    ></span>
  </span>
);

// Construiește ruta + label pentru butonul de „următorul pas" în funcție de acțiune.
const buildNextStepCTA = (nextStep, t) => {
  if (!nextStep) return null;
  const { action, courseId, trainingId } = nextStep;
  switch (action) {
    case 'continue':
      return { to: `/training/${courseId}`, label: t('compliance.ctaContinue') };
    case 'start':
      return { to: `/training/${courseId}`, label: t('compliance.ctaStart') };
    case 'retake':
      return { to: `/training/${courseId}`, label: t('compliance.ctaRetake') };
    case 'sign':
      return {
        to: trainingId ? `/training/${trainingId}/sign` : `/my-courses`,
        label: t('compliance.ctaSign'),
      };
    default:
      return { to: '/my-courses', label: t('compliance.ctaViewCourses') };
  }
};

// Formatare zile rămase pentru scadențe IP.
const formatDue = (item, t) => {
  if (item.neverTrained) return t('compliance.neverTrained');
  if (item.daysUntilDue == null) return '';
  if (item.daysUntilDue < 0)
    return t('compliance.expiredDaysAgo', { count: Math.abs(item.daysUntilDue) });
  if (item.daysUntilDue === 0) return t('compliance.dueToday');
  return t('compliance.dueInDays', { count: item.daysUntilDue });
};

// Widget „Sunt eu în regulă?" — semafor de conformitate PERSONALĂ + „următorul pas" + scadențe.
// Se auto-ascunde (return null) dacă userul nu are profil de angajat sau nu are nimic de monitorizat
// (fără cursuri și fără IP-uri) — ca să nu apară pentru SUPER_ADMIN / SSM fără employee.
const PersonalComplianceWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    dashboardAPI
      .getMyCompliance()
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (!data || !data.hasEmployee) return null;

  const hasCourses = (data.courses || []).length > 0;
  const hasIP = (data.ip || []).length > 0;
  // Nimic de monitorizat → nu afișa widget-ul.
  if (!hasCourses && !hasIP) return null;

  const status = data.overallStatus || 'green';
  const cta = buildNextStepCTA(data.nextStep, t);

  // Culori pe baza statusului agregat.
  const statusStyles = {
    red: 'border-danger-300 bg-danger-50 dark:border-danger-800 dark:bg-danger-950/30',
    yellow: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    green: 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/20',
  };
  const statusText = {
    red: 'text-danger-800 dark:text-danger-300',
    yellow: 'text-amber-800 dark:text-amber-300',
    green: 'text-emerald-800 dark:text-emerald-300',
  };
  const statusHeadline = {
    red: t('compliance.statusRed'),
    yellow: t('compliance.statusYellow'),
    green: t('compliance.statusGreen'),
  };

  // Termene urgente — instruiri atribuite cu dueDate ≤14 zile sau depășit, neîncă promovate.
  // Afișate ca banner vizibil deasupra cardurilor de conformitate când există. Anterior
  // angajatul nu vedea deadline-urile nicăieri pe dashboard.
  const urgentDeadlines = (data.courses || [])
    .filter((c) => c.dueDate && c.status !== 'PASSED')
    .map((c) => {
      const due = new Date(c.dueDate);
      const days = Math.ceil((due - new Date()) / 86400000);
      return { ...c, days, due };
    })
    .filter((c) => c.days <= 14)
    .sort((a, b) => a.days - b.days)
    .slice(0, 5);

  return (
    <div data-onboarding="personal-compliance" className="space-y-4">
      {urgentDeadlines.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/my-courses')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/my-courses');
            }
          }}
          className="rounded-lg border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30 p-4 cursor-pointer hover:shadow-md transition"
        >
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-red-900 dark:text-red-200">
                {t('compliance.urgentDeadlinesTitle')}
              </p>
              <ul className="mt-1 space-y-0.5">
                {urgentDeadlines.map((c) => (
                  <li
                    key={c.courseId}
                    className="text-xs text-red-800 dark:text-red-300 flex items-center gap-2"
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span className="font-medium">{c.title}</span>
                    <span>—</span>
                    <span className="font-semibold">
                      {c.days < 0
                        ? t('compliance.deadlineOverdue', { count: -c.days })
                        : c.days === 0
                          ? t('compliance.deadlineToday')
                          : t('compliance.deadlineInDays', { count: c.days })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card 1 — Semafor „Sunt eu în regulă?" + scadențe care expiră curând */}
        <div className={`rounded-lg border p-4 ${statusStyles[status]}`}>
          <div className="flex items-start gap-3">
            <MiniTrafficLight status={status} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                {t('compliance.amIOk')}
                <HelpTooltip text={t('common.glossaryAbbr')} size={13} />
              </p>
              <p className={`text-sm font-semibold ${statusText[status]}`}>
                {statusHeadline[status]}
              </p>

              {/* Lista „Expiră curând" — doar dacă există scadențe galbene/roșii */}
              {(data.expiringSoon || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {data.expiringSoon.slice(0, 4).map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.status === 'red' ? 'bg-danger-500' : 'bg-amber-500'}`}
                      ></span>
                      <span className="text-slate-700 dark:text-slate-300 font-medium">
                        {t('compliance.ipLabel', { service: item.service })}
                      </span>
                      <span
                        className={
                          item.status === 'red'
                            ? 'text-danger-700 dark:text-danger-300'
                            : 'text-amber-700 dark:text-amber-300'
                        }
                      >
                        {formatDue(item, t)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {status === 'green' && (
                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                  {t('compliance.allGood')}
                </p>
              )}

              <button
                onClick={() => navigate('/my-courses')}
                className="mt-2 text-xs text-primary-600 dark:text-primary-300 hover:underline"
              >
                {t('compliance.viewMyTrainings')} →
              </button>
            </div>
          </div>
        </div>

        {/* Card 2 — „Următorul pas": acțiunea imediată + bară de progres */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('compliance.nextStep')}
          </p>
          {data.nextStep ? (
            <>
              <p className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-1">
                {TYPE_SHORT[data.nextStep.type] ? `${TYPE_SHORT[data.nextStep.type]} — ` : ''}
                {data.nextStep.title}
              </p>
              {data.nextStep.progress && data.nextStep.progress.total > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                    <span>
                      {t('compliance.moduleProgress', {
                        done: data.nextStep.progress.completed,
                        total: data.nextStep.progress.total,
                      })}
                    </span>
                    <span className="tabular-nums">{data.nextStep.progress.percent}%</span>
                  </div>
                  <ProgressBar
                    completed={data.nextStep.progress.completed}
                    total={data.nextStep.progress.total}
                    percent={data.nextStep.progress.percent}
                  />
                </div>
              )}
              {cta && (
                <button
                  onClick={() => navigate(cta.to)}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium transition"
                >
                  {cta.label} →
                </button>
              )}
            </>
          ) : (
            <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-300">
              {t('compliance.nothingToDo')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalComplianceWidget;
